import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  PlayIcon,
  PauseIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentDuplicateIcon,
  CreditCardIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  UserPlusIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getLocalApiUrl, authFetch } from '../../config/api'


function RulesList() {
  const [rules, setRules] = useState([])
  const [filteredRules, setFilteredRules] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedSeverity, setSelectedSeverity] = useState('all')
  const [showFilters, setShowFilters] = useState(false)
  const [activeTab, setActiveTab] = useState('published') // 'published' | 'draft'
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [assignModal, setAssignModal] = useState({
    open: false,
    ruleId: null,
    venues: [],
    selected: new Set(),
    initialAssigned: new Set()
  })

  useEffect(() => {
    const fetchRules = async () => {
      setIsLoading(true)
      setLoadError('')
      try {
        const statusParam = activeTab === 'draft' ? 'draft' : 'published'
        const resp = await authFetch(getLocalApiUrl(`/api/rules/db?status=${statusParam}`))
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const json = await resp.json()
        if (json?.success && Array.isArray(json.data)) {
          const matchesDeprecatedSesE5 = (condsRaw) => {
            try {
              const conds = Array.isArray(condsRaw) ? condsRaw : JSON.parse(condsRaw || '[]')
              if (!Array.isArray(conds) || conds.length < 6) return false
              const toKey = (c) => (String(c.label || c.field || c.left || c.name || '').toLowerCase())
              const toOp = (c) => (String(c.operator || c.op || '').toLowerCase())
              const toVal = (c) => Number(c.value ?? c.right ?? c.threshold ?? c.val ?? NaN)
              const want = [
                { k: 'current period session count', op: 'greater_than', v: 10 },
                { k: 'previous period session count', op: 'greater_than', v: 10 },
                { k: 'current period avg turnover', op: 'greater_than', v: 100 },
                { k: 'previous period avg turnover', op: 'greater_than', v: 100 },
                { k: 'turnover change (%)', op: 'greater_than_or_equal', v: 200 },
                { k: 'win % change (pp)', op: 'less_than_or_equal', v: -200 }
              ]
              return want.every(w => conds.some(c => toKey(c).includes(w.k) && toOp(c) === w.op && toVal(c) === w.v))
            } catch { return false }
          }

          let normalized = json.data.map(r => ({
            id: r.rule_id || r.id,
            name: r.name,
            type: r.type,
            category: r.category,
            severity: (r.severity || '').toString().toUpperCase(),
            enabled: (r.enabled !== undefined ? !!r.enabled : true),
            description: r.description || '',
            rationale: r.rationale ?? '',
            rationale_source: r.rationale_source ?? null,
            processing_category: r.processing_category || null,
            triggerCount: r.triggerCount ?? 0,
            lastTriggered: r.lastTriggered ?? '—',
            createdAt: r.createdAt || '',
            _conditions: r.conditions
          }))

          normalized = normalized.filter(r => !(String(r.id).toUpperCase() === 'SES_E5_UNUSUAL_BEHAVIOUR' && matchesDeprecatedSesE5(r._conditions)))
          normalized = normalized.map(({ _conditions, ...rest }) => rest)

          setRules(normalized)
          setFilteredRules(normalized)
        } else {
          setRules([])
          setFilteredRules([])
        }
      } catch (err) {
        setLoadError('Failed to load rules from server')
        setRules([])
        setFilteredRules([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchRules()
  }, [activeTab])

  useEffect(() => {
    filterRules()
  }, [searchTerm, selectedCategory, selectedType, selectedSeverity, rules])

  const filterRules = () => {
    let filtered = rules
    if (searchTerm) {
      filtered = filtered.filter(rule => 
        rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (rule.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    if (selectedCategory !== 'all') filtered = filtered.filter(rule => rule.category === selectedCategory)
    if (selectedType !== 'all') filtered = filtered.filter(rule => rule.type === selectedType)
    if (selectedSeverity !== 'all') filtered = filtered.filter(rule => rule.severity === selectedSeverity)
    setFilteredRules(filtered)
  }

  /** Published list: only flag empty/short rationale. Draft list: also flag backfill (submit requires author source). */
  const ruleNeedsRationaleBadge = (rule) => {
    const rat = String(rule.rationale ?? '').trim()
    const tooShort = rat.length < 20
    const isBackfill = rule.rationale_source === 'backfill'
    if (activeTab === 'draft') return tooShort || isBackfill
    return tooShort
  }

  const toggleRuleStatus = async (ruleId) => {
    try {
      setRules(rules.map(rule => 
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      ))
      toast.success('Rule status updated')
    } catch (error) {
      toast.error('Failed to update rule status')
    }
  }

  const deleteRule = async (ruleId) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      try {
        setRules(rules.filter(rule => rule.id !== ruleId))
        toast.success('Rule deleted successfully')
      } catch (error) {
        toast.error('Failed to delete rule')
      }
    }
  }

  const setPublishStatus = async (ruleId, status) => {
    try {
      const statusVal = status === 'published' ? 'published' : 'draft'
      const resp = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(ruleId)}/publish`), {
        method: 'POST', headers: { 'Content-Type':'application/json' }, body: JSON.stringify({ status: statusVal })
      })
      const j = await resp.json()
      if (!resp.ok || !j.success) throw new Error(j.error||'Failed')
      toast.success(status==='published'?'Published':'Moved to Draft')
      const r = await authFetch(getLocalApiUrl(`/api/rules/db?status=${activeTab === 'draft' ? 'draft' : 'published'}`))
      const j2 = await r.json()
      if (j2?.success) {
        const normalized = (j2.data || []).map((rr) => ({
          id: rr.rule_id || rr.id,
          name: rr.name,
          type: rr.type,
          category: rr.category,
          severity: (rr.severity || '').toString().toUpperCase(),
          enabled: rr.enabled !== undefined ? !!rr.enabled : true,
          description: rr.description || '',
          rationale: rr.rationale ?? '',
          rationale_source: rr.rationale_source ?? null,
          processing_category: rr.processing_category || null,
          triggerCount: rr.triggerCount ?? 0,
          lastTriggered: rr.lastTriggered ?? '—',
          createdAt: rr.createdAt || ''
        }))
        setRules(normalized)
        setFilteredRules(normalized)
      }
    } catch (_) { toast.error('Failed to change publish status') }
  }

  const duplicateRule = async (rule) => {
    try {
      const newRule = {
        ...rule,
        id: `${rule.type.toUpperCase()}_${Date.now()}`,
        name: `${rule.name} (Copy)`,
        createdAt: new Date().toISOString(),
        triggerCount: 0,
        lastTriggered: 'Never'
      }
      setRules([newRule, ...rules])
      toast.success('Rule duplicated successfully')
    } catch (error) {
      toast.error('Failed to duplicate rule')
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-red-100 text-red-800'
      case 'HIGH': return 'bg-orange-100 text-orange-800'
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800'
      case 'LOW': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case 'AML': return 'bg-red-50 text-red-700 border-red-200'
      case 'UAR': return 'bg-yellow-50 text-yellow-700 border-yellow-200'
      case 'RG': return 'bg-green-50 text-green-700 border-green-200'
      case 'COMPLIANCE': return 'bg-blue-50 text-blue-700 border-blue-200'
      default: return 'bg-gray-50 text-gray-700 border-gray-200'
    }
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'transaction': return CreditCardIcon
      case 'session': return ChartBarIcon
      case 'cumulative': return ChartBarIcon
      case 'compliance': return ShieldCheckIcon
      case 'onboarding': return UserPlusIcon
      default: return DocumentDuplicateIcon
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rules Management</h1>
          <p className="text-sm text-gray-500">Manage and monitor your club transaction rules</p>
        </div>
        <Link to="/monitor-safe/builder/combined" className="btn-primary flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Rule
        </Link>
      </div>

      {isLoading && <div className="card text-sm text-gray-600">Loading rules…</div>}
      {!isLoading && loadError && (
        <div className="card text-sm text-yellow-700 bg-yellow-50 border border-yellow-200">{loadError}</div>
      )}

      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search rules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary flex items-center">
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
            <div className="text-sm text-gray-500">{filteredRules.length} of {rules.length} rules</div>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="select-field">
                  <option value="all">All Categories</option>
                  <option value="AML">Anti-Money Laundering</option>
                  <option value="UAR">Unusual Activity Reporting</option>
                  <option value="RG">Responsible Gaming</option>
                  <option value="COMPLIANCE">Regulatory Compliance</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="select-field">
                  <option value="all">All Types</option>
                  <option value="transaction">Transaction</option>
                  <option value="session">Session</option>
                  <option value="cumulative">Cumulative</option>
                  <option value="compliance">Compliance</option>
                  <option value="onboarding">Onboarding</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                <select value={selectedSeverity} onChange={(e) => setSelectedSeverity(e.target.value)} className="select-field">
                  <option value="all">All Severities</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex gap-4">
          <button className={`px-3 py-2 text-sm ${activeTab==='published'?'text-psg-teal border-b-2 border-psg-teal font-semibold':'text-gray-600'}`} onClick={()=>setActiveTab('published')}>Published</button>
          <button className={`px-3 py-2 text-sm ${activeTab==='draft'?'text-psg-teal border-b-2 border-psg-teal font-semibold':'text-gray-600'}`} onClick={()=>setActiveTab('draft')}>Draft</button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type & Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRules.map((rule) => (
                <tr key={rule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {(() => { const Icon = getTypeIcon(rule.type); return (<Icon className="w-5 h-5 text-gray-500 mr-2" />) })()}
                      <div>
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                          {rule.name}
                          {/* S3 — draft: backfill must be re-saved as author before submit. Published: only empty/short. */}
                          {ruleNeedsRationaleBadge(rule) && (
                            <>
                              <span
                                title={
                                  String(rule.rationale ?? '').trim().length < 20
                                    ? 'Rationale missing — rule cannot be submitted until a rationale of at least 20 characters is authored.'
                                    : 'Rationale was backfilled from description — open the rule and save an authored rationale before submit.'
                                }
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                              >
                                ⚠ Needs rationale
                              </span>
                              <Link
                                to={`/monitor-safe/builder/combined?id=${encodeURIComponent(rule.id)}&focus=rationale`}
                                className="text-[11px] font-semibold text-amber-900 hover:text-amber-950 underline underline-offset-2"
                              >
                                Add rationale
                              </Link>
                            </>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{rule.id}</div>
                        <div className="text-xs text-gray-400 mt-1">{rule.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">{rule.type}</span>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(rule.category)}`}>{rule.category}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(rule.severity)}`}>{rule.severity}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${rule.enabled ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                      <span className={`text-sm ${rule.enabled ? 'text-green-600' : 'text-gray-500'}`}>{rule.enabled ? 'Active' : 'Inactive'}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>
                      <div className="font-medium text-gray-900">{rule.triggerCount} triggers</div>
                      <div className="text-xs">Last: {rule.lastTriggered}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {activeTab==='published' && (
                        <button onClick={() => toggleRuleStatus(rule.id)} className={`p-2 rounded-lg ${rule.enabled ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`} title={rule.enabled ? 'Disable rule' : 'Enable rule'}>
                          {rule.enabled ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
                        </button>
                      )}
                      <button onClick={() => duplicateRule(rule)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Duplicate rule">
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      <button onClick={async()=>{
                        try {
                          const [allRes, assignedRes] = await Promise.all([
                            authFetch(getLocalApiUrl('/api/venues')),
                            authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(rule.id)}/venues`))
                          ])
                          const allJson = allRes.ok ? await allRes.json() : {}
                          const asgJson = assignedRes.ok ? await assignedRes.json() : {}
                          const allVenues = Array.isArray(allJson.data) ? allJson.data : []
                          const assigned = Array.isArray(asgJson.data) ? asgJson.data : []
                          const assignedIds = new Set(assigned.map(x => String(x.id)))
                          setAssignModal({
                            open: true,
                            ruleId: rule.id,
                            venues: allVenues,
                            selected: new Set(assignedIds),
                            initialAssigned: new Set(assignedIds)
                          })
                        } catch { toast.error('Failed to load venues') }
                      }} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg" title="Assign venues">
                        <UserGroupIcon className="h-4 w-4" />
                      </button>
                      <Link to={`/monitor-safe/builder/combined?id=${encodeURIComponent(rule.id)}`} className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg" title="Edit rule">
                        <PencilIcon className="h-4 w-4" />
                      </Link>
                      {activeTab==='draft' ? (
                        <button onClick={()=>setPublishStatus(rule.id, 'published')} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Publish">
                          <PlayIcon className="h-4 w-4" />
                        </button>
                      ) : (
                        <button onClick={()=>setPublishStatus(rule.id, 'draft')} className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg" title="Move to Draft">
                          <PauseIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => deleteRule(rule.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete rule">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRules.length === 0 && (
          <div className="text-center py-12">
            <DocumentDuplicateIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No rules found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || selectedCategory !== 'all' || selectedType !== 'all' || selectedSeverity !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first rule.'}
            </p>
            {!searchTerm && selectedCategory === 'all' && selectedType === 'all' && selectedSeverity === 'all' && (
              <Link to="/monitor-safe/builder/combined" className="btn-primary inline-flex items-center">
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Your First Rule
              </Link>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-blue-600">{rules.filter(r => r.enabled).length}</div>
          <div className="text-sm text-gray-500">Active Rules</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-red-600">{rules.filter(r => r.severity === 'CRITICAL').length}</div>
          <div className="text-sm text-gray-500">Critical Rules</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-green-600">{rules.reduce((sum, r) => sum + (Number(r.triggerCount)||0), 0)}</div>
          <div className="text-sm text-gray-500">Total Triggers</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-purple-600">{rules.filter(r => (r.processing_category||'').toLowerCase() === 'cumulative').length}</div>
          <div className="text-sm text-gray-500">Cumulative Rules</div>
        </div>
      </div>

      {assignModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex">
          <div className="m-auto w-full max-w-2xl bg-white rounded shadow-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="text-base font-medium">Assign Venues to Rule</div>
              <button className="text-gray-600" onClick={()=>setAssignModal({ open:false, ruleId:null, venues:[], selected:new Set(), initialAssigned:new Set() })}>Close</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm text-gray-700">Rule ID: <span className="font-mono">{assignModal.ruleId}</span></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(assignModal.venues||[]).map(v => (
                  <label key={v.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={assignModal.selected.has(String(v.id))} onChange={(e)=>{
                      setAssignModal(prev=>{ const next = new Set(prev.selected); if (e.target.checked) next.add(String(v.id)); else next.delete(String(v.id)); return { ...prev, selected: next } })
                    }} />
                    <span>{v.name ?? v.venue_id ?? v.code ?? `Venue ${v.id}`}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className="btn-secondary" onClick={()=>setAssignModal({ open:false, ruleId:null, venues:[], selected:new Set(), initialAssigned:new Set() })}>Cancel</button>
              <button className="btn-primary" onClick={async()=>{
                try {
                  const initial = assignModal.initialAssigned || new Set()
                  const next = assignModal.selected
                  const toAdd = [...next].filter(x => !initial.has(x))
                  const toRemove = [...initial].filter(x => !next.has(x))
                  for (const vid of toAdd) { await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(assignModal.ruleId)}/venues`), { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ venue_id: vid }) }) }
                  for (const vid of toRemove) { await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(assignModal.ruleId)}/venues/${encodeURIComponent(vid)}`), { method:'DELETE' }) }
                  setAssignModal({ open:false, ruleId:null, venues:[], selected:new Set(), initialAssigned:new Set() })
                  toast.success('Assignments updated')
                } catch { toast.error('Failed to update assignments') }
              }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RulesList
